import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DisplayType } from '@models/module/fields/enums';
import { AddressModel, getAddressModelFromAzureMapAddressAndPosition } from '@models/module/fields/main';
import { AzureMapPosition } from '@shared/components/azure-map/azure-map.model';
import { AzureMapService } from '@shared/components/azure-map/azure-map.service';
import { HttpService } from '@shared/services/http.service';
import { NotificationService } from '@shared/services/notification.service';
import { UserService } from '@shared/services/user.service';
import { catchError, map, Observable, of, switchMap, throwError } from 'rxjs';
import { CipoFieldConfig } from '../common';
import { CipoAddressDialogComponent, CipoAddressDialogData } from './cipo-address-dialog/cipo-address-dialog.component';
import {
  CipoGlobalAddressesDialogComponent,
  CipoGlobalAddressesDialogData,
} from './cipo-global-addresses-dialog/cipo-global-addresses-dialog.component';

export type SyncGlobalAddressesRequest = {
  upserted: AddressModel[];
  deletedLocationIds: number[];
};

@Injectable({
  providedIn: 'root',
})
export class CipoAddressService {
  private http = inject(HttpService);
  private dialog = inject(MatDialog);
  private notificationService = inject(NotificationService);
  private azureMapService = inject(AzureMapService);
  private userService = inject(UserService);

  getAddress(locationId: number): Observable<AddressModel> {
    const params = this.http.buildHttpParams({ locationId });
    return this.http
      .get<AddressModel>('_api/Address/GetAddress', {
        httpOptions: { params },
        useLoader: false,
        useErrorNotification: false,
      })
      .pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404) {
            this.notificationService.error('MODULE_DEF_ADDRESS.NOT_FOUND');
          } else {
            this.notificationService.error(err);
          }
          return throwError(() => err);
        }),
      );
  }

  /** Retrieves all global addresses for the current tenant. */
  getGlobalAddresses(): Observable<AddressModel[]> {
    return this.http.get<AddressModel[]>('_api/Address/GlobalAddresses', {
      useLoader: false,
      useErrorNotification: true,
    });
  }

  /**
   * Bulk-syncs global addresses. Upserts new/updated entries and deletes/demotes removed ones.
   * Returns the refreshed list of all global addresses after the sync.
   */
  syncGlobalAddresses(request: SyncGlobalAddressesRequest): Observable<AddressModel[]> {
    return this.http.post<AddressModel[]>('_api/Address/SyncGlobalAddresses', request, {
      useLoader: true,
      useErrorNotification: true,
    });
  }

  /**
   * Formats an address value for display according to the field's configured visualization mode.
   * @param address The address model to format.
   * @param displayType The `DisplayType` value from `CipoFieldBaseConfig`.
   *   - `DisplayType.AddressLocationName` (`19`) — location name only
   *   - `DisplayType.AddressOnly` (`20`) — address string only
   *   - `DisplayType.AddressAutocomplete` (`21`) — address string only (no location name)
   *   - any other value (incl. `undefined` / `DisplayType.Address`) — location name + address (default)
   */
  formatAddress(address: AddressModel, displayType: DisplayType | undefined): string | undefined {
    if (!address) return undefined;
    if (displayType === DisplayType.AddressLocationName) return address.locationName;
    if (displayType === DisplayType.AddressOnly || displayType === DisplayType.AddressAutocomplete) return address.address;
    return address.locationName ? `${address.locationName} (${address.address})` : address.address;
  }

  formatPosition(address: AddressModel) {
    return `${address.lat}, ${address.lon}`;
  }

  /**
   * Returns `true` when all required address fields are present and non-zero.
   * Shared by both address dialogs to avoid duplicated validation logic.
   */
  addressIsValid(address: AddressModel): boolean {
    return !!address.address && !!address.lat && !!address.lon && !!address.locationName;
  }

  /**
   * Performs reverse geocoding for the given coordinates.
   * Merges the result into `currentAddress`, preserving `locationId`, `locationName`, and `isGlobal`.
   * Falls back to updating only `lat`/`lon` on error.
   */
  reverseGeocode(position: AzureMapPosition, currentAddress: AddressModel): Observable<AddressModel> {
    const azureMapService = this.azureMapService;
    const { lat, lon } = position;
    if (!lat || !lon) {
      return of({ ...currentAddress, lat, lon });
    }
    return azureMapService.getAddressFromCoordinates(lat, lon).pipe(
      map(result => {
        if (!result?.addresses?.length || !result.addresses[0].address) {
          return { ...currentAddress, lat, lon };
        }
        const { address } = result.addresses[0];
        return {
          ...getAddressModelFromAzureMapAddressAndPosition(address, position),
          locationId: currentAddress?.locationId,
          locationName: currentAddress?.locationName,
          isGlobal: currentAddress?.isGlobal,
        };
      }),
      catchError(() => of({ ...currentAddress, lat, lon })),
    );
  }

  /**
   * Opens the global addresses management dialog.
   * @param initialAddresses The current global addresses to pre-populate the dialog with.
   * @returns An observable that emits the refreshed global address list when the dialog is saved, or `undefined` on cancel.
   */
  openGlobalAddressesDialog(initialAddresses: AddressModel[]): Observable<AddressModel[] | undefined> {
    const dialogRef = this.dialog.open<
      CipoGlobalAddressesDialogComponent,
      CipoGlobalAddressesDialogData,
      AddressModel[]
    >(CipoGlobalAddressesDialogComponent, {
      data: { globalAddresses: initialAddresses },
      panelClass: ['cipo-dialog', 'classic', 'cipo--height--full'],
      ...this.userService.getResponsiveDialogSize().xl,
      maxHeight: '100vh',
      maxWidth: '100vw',
    });
    return dialogRef.afterClosed();
  }

  /**
   * Fetches global addresses from the API and opens the global addresses management dialog.
   * Combines `getGlobalAddresses` and `openGlobalAddressesDialog` to avoid duplicating the fetch+open pattern at call sites.
   * @returns An observable that emits the refreshed global address list when saved, or `undefined` when cancelled.
   * The dialog only closes with data when the user explicitly saves, so a defined result always means changes were made.
   */
  openGlobalAddressesDialogWithoutData(): Observable<AddressModel[] | undefined> {
    return this.getGlobalAddresses().pipe(
      switchMap(addresses => this.openGlobalAddressesDialog(addresses)),
    );
  }

  openMap(config: CipoFieldConfig, addresses: AddressModel[]) {
    const editMode = !(config?.readonly ?? false);
    const multiple = config?.multiple ?? false;
    const label = config?.label ?? '';

    const dialogRef = this.dialog.open<CipoAddressDialogComponent, CipoAddressDialogData, AddressModel[]>(
      CipoAddressDialogComponent,
      {
        data: {
          addresses,
          editMode,
          multiple,
          label,
          displayType: config?.displayType ?? DisplayType.Address,
        },
        panelClass: ['cipo-dialog', 'classic', 'cipo--height--full'],
        ...this.userService.getResponsiveDialogSize().xl,
        maxHeight: '100vh',
        maxWidth: '100vw',
      },
    );

    return dialogRef.afterClosed();
  }

  getAddressAndOpenMap(title: string, locationId: number) {
    return this.getAddress(locationId).pipe(
      switchMap(address => this.openMap({ readonly: true, label: title }, [address])),
    );
  }
}
